import React from "react";
import {RunInAction} from "web-vcore";
import {store} from "../../../../../Store/index.js";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";
import {MapTool, Tool, ToolData, ToolInterceptSubresult} from "./@Tool.js";
import {MapDragInfo} from "./Helpers/DragHelper.js";
import {Button, Row, Spinner, Text, TextInput} from "react-vcomponents";
import {Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {action} from "mobx";
import {O} from "web-vcore";
import {GetAngleInDegreesOfLine} from "../../../../../Utils/ThreeJS/SpaceUtils.js";
import {MapSurface_current} from "../../MapSurface.js";
import {observer_mgl} from "mobx-graphlink";
import {terrainQuadtree_current} from "../../../../../Terrain/Heights/TerrainRenderer.js";
import {ignore} from "mobx-sync";

/*export class MeasureExtras {
	@O @ignore accessor currentMousePos_world: Vec3|n;
}*/

export class ToolState_Measure {
	GetMeasurePointsToDisplay() {
		const measurePointsToDisplay = this.currentDrag?.currentPos_world ? [this.currentDrag.startPos_world, this.currentDrag.currentPos_world] : this.measurePoints;
		return measurePointsToDisplay;
	}

	@O @ignore accessor measurePoints: [Vec3, Vec3]|n;

	@O @ignore accessor currentDrag: MapDragInfo|n;
	@action EndCurrentDrag() {
		if (this.currentDrag == null) return;
		this.currentDrag = null;
	}
}

export class Tool_Measure extends Tool {
	toolType = MapTool.measure;
	get ToolState() { return store.main.maps.tools.toolStates.measure; }

	RenderInToolbox = observer_mgl(({data}: {data: ToolData.RenderInToolbox})=>{
		const state = this.ToolState;
		const measurePoints = state.GetMeasurePointsToDisplay();
		return (
			<>
				{measurePoints != null && <>
					<Text>From:</Text>
					<TextInput ml={5} enabled={false} style={{width: 150}} value={measurePoints[0].AsArray().slice(0, 2).map(a=>a.toFixed(2)).join(", ")}/>
					<Text ml={5}>To:</Text>
					<TextInput ml={5} enabled={false} style={{width: 150}} value={measurePoints[1].AsArray().slice(0, 2).map(a=>a.toFixed(2)).join(", ")}/>
					<Button ml={5} mdIcon="delete-sweep" size={25} title="Clear from and to points" onClick={()=>{
						RunInAction("Tool_Measure.Clear", ()=>state.measurePoints = null);
					}}/>
					<Text ml={5}>Distance:</Text>
					<Text ml={5}>{measurePoints[0].ToVec2().DistanceTo(measurePoints[1].ToVec2()).toFixed(2)}m</Text>
					<Text ml={5}>Angle:</Text>
					<Text ml={5}>{GetAngleInDegreesOfLine(measurePoints[0].ToVec2(), measurePoints[1].ToVec2()).toFixed(2)}°</Text>
					{terrainQuadtree_current != null && (()=>{
						const elevStart = terrainQuadtree_current.sampleHeightAt(measurePoints[0].x, measurePoints[0].y);
						const elevEnd = terrainQuadtree_current.sampleHeightAt(measurePoints[1].x, measurePoints[1].y);
						return <Row>
							<Text ml={5}>Elev, start:</Text>
							<Text ml={5}>{elevStart != null ? `${elevStart.toFixed(1)}m` : "—"}</Text>
							<Text ml={5}>End:</Text>
							<Text ml={5}>{elevEnd != null ? `${elevEnd.toFixed(1)}m` : "—"}</Text>
							{elevStart != null && elevEnd != null && <>
								<Text ml={5}>Δ:</Text>
								<Text ml={5}>{(elevEnd - elevStart).toFixed(1)}m</Text>
							</>}
						</Row>;
					})()}
				</>}
			</>
		);
	});
	RenderInSceneOverlay = observer_mgl((props: {data: ToolData.RenderInSceneOverlay})=>{
		const {mapState} = props.data;
		const state = this.ToolState;
		const measurePoints_world = state.GetMeasurePointsToDisplay();
		const mapControls_DOM = MapSurface_current?.mapControls?.DOM;
		if (measurePoints_world == null || mapControls_DOM == null) return null;
		const measurePoints_ui = measurePoints_world?.map(a=>mapControls_DOM.WorldPosToClientPos(a.ToVec2()));

		// extra accessors (to recalculate the world->ui translation, if map pan/zoom changes)
		mapState.camPos_0ms; // eslint-disable-line
		mapState.camZoom_0ms; // eslint-disable-line

		const deltaX = measurePoints_ui[1].x - measurePoints_ui[0].x;
		const deltaY = measurePoints_ui[1].y - measurePoints_ui[0].y;
		const length = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
		const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);

		/*return (
			<g>
				<line x1={measurePoints[0].x} y1={measurePoints[0].y} x2={measurePoints[1].x} y2={measurePoints[1].y} stroke="white" strokeWidth="2"/>
				<circle cx={measurePoints[0].x} cy={measurePoints[0].y} r="5" fill="white"/>
				<circle cx={measurePoints[1].x} cy={measurePoints[1].y} r="5" fill="white"/>
			</g>
		);*/
		return <div style={{
			position: "fixed",
			border: "none",
			borderTop: "2px solid white",
			pointerEvents: "none",
			zIndex: 100,
			left: `${measurePoints_ui[0].x}px`,
			top: `${measurePoints_ui[0].y}px`,
			height: "0px",
			transformOrigin: "0 0",
			width: `${length}px`,
			transform: `rotate(${angle}deg)`,
		}}/>;
	});

	Document_OnKeyDown(data: ToolData.Document_OnKeyDown): ToolInterceptSubresult | void {
		if (data.event.key == "Escape") {
			this.ToolState.EndCurrentDrag();
			return {absorb: true};
		}
	}

	Scene_OnPointerDown(data: ToolData.Scene_OnPointerClick) {
		const {event, mousePos, mousePos_world, controls} = data;
		if (event.button != 0) return;

		const state = this.ToolState;
		if (state.currentDrag == null) {
			RunInAction("Measure.Scene_OnPointerDown", ()=>{
				state.currentDrag = new MapDragInfo({
					button: event.button,
					startPos_mouse: mousePos,
					startPos_world: mousePos_world,
					currentPos_mouse: mousePos,
					currentPos_world: mousePos_world,
					//extras: new MeasureExtras(),
				});
			});
		}
	}

	Document_OnPointerMove(data: ToolData.Document_OnPointerMove) {
		const {event, mousePos, mousePos_world, controls} = data;
		const state = this.ToolState;
		const drag = state.currentDrag;
		if (drag) {
			RunInAction("Measure.Document_OnPointerMove", ()=>{
				drag.currentPos_mouse = mousePos;
				drag.currentPos_world = mousePos_world;
			});
		}
	}

	Document_OnPointerUp(data: ToolData.Document_OnPointerUp) {
		const {event, mousePos, mousePos_world, controls} = data;
		const state = this.ToolState;
		const drag = state.currentDrag;
		if (drag?.button == event.button) {
			const startWorld = new Vec3(drag.startPos_world.x, drag.startPos_world.y, 0);
			const endWorld = new Vec3(mousePos_world.x, mousePos_world.y, 0);
			state.measurePoints = [startWorld, endWorld];
			this.ToolState.EndCurrentDrag();
		}
	}
}